'use client'

import type { ReactNode } from 'react'

import { InfoTooltip } from '@morpho-org/ui-v6/client'
import { Skeleton } from '@morpho-org/ui-v6/server'
import { cn } from '@morpho-org/ui-v6/utils'

interface VaultV2RowListSectionProps {
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  id?: string
  titleClassName?: string
}

interface VaultV2RowListProps {
  children: ReactNode
  className?: string
}

interface VaultV2RowListRowProps {
  label: ReactNode
  children: ReactNode
  className?: string
  description?: ReactNode
  labelAddon?: ReactNode
  tooltip?: ReactNode
  valueClassName?: string
}

export const VaultV2RowList = ({ children, className }: VaultV2RowListProps) => {
  return (
    <div
      className={cn(
        'shadow-md dark:shadow-none border-border border-[0.5px] bg-card px-sm flex w-full flex-col rounded-lg',
        className
      )}
    >
      {children}
    </div>
  )
}

export const VaultV2RowListSection = ({
  title,
  description,
  children,
  className,
  id,
  titleClassName
}: VaultV2RowListSectionProps) => {
  return (
    <div id={id} className={cn('gap-sm flex flex-col', className)}>
      <div className="gap-2xs flex flex-col">
        <span className={cn(titleClassName ?? 'text-sm', 'text-foreground')}>{title}</span>
        {description && <span className="text-2xs text-secondary-foreground">{description}</span>}
      </div>
      <VaultV2RowList>{children}</VaultV2RowList>
    </div>
  )
}

export const VaultV2RowListRow = ({
  label,
  children,
  className,
  description,
  labelAddon,
  tooltip,
  valueClassName
}: VaultV2RowListRowProps) => {
  return (
    <div
      className={cn(
        'gap-md border-border border-b-[0.5px] py-xs flex min-h-14 items-center justify-between last:border-b-0',
        className
      )}
    >
      <div className="gap-2xs flex min-w-0 flex-col">
        <div className="gap-2xs flex items-center">
          <span className="text-foreground text-xs">{label}</span>
          {tooltip && (
            <InfoTooltip
              tooltipContent={tooltip}
              className="text-secondary-foreground hover:text-foreground transition-colors"
            />
          )}
          {labelAddon}
        </div>
        {description && <span className="text-2xs text-secondary-foreground">{description}</span>}
      </div>
      <div className={cn('gap-2xs flex shrink-0 items-center text-right', valueClassName)}>
        {children}
      </div>
    </div>
  )
}

export const VaultV2RowListSkeletonRow = ({
  withDescription = false
}: {
  withDescription?: boolean
}) => {
  return (
    <div className="gap-md border-border py-xs flex min-h-14 items-center justify-between border-b-[0.5px] last:border-b-0">
      <div className="gap-2xs flex min-w-0 flex-1 flex-col">
        <Skeleton className="h-(--text-xs--line-height) w-[160px]" />
        {withDescription && <Skeleton className="h-(--text-2xs--line-height) w-3/4" />}
      </div>
      <Skeleton className="h-(--text-xs--line-height) w-[64px]" />
    </div>
  )
}
